// Text-only passenger barks. No audio assets or voice generation are associated with these lines.
import type { PassengerKind } from '../sim/fares';
import type { DisasterKind } from '../sim/disasters';

export type PassengerEvent = 'badDriving' | 'speeding' | DisasterKind;
export const PASSENGER_LINES: Record<PassengerKind, Record<PassengerEvent, readonly string[]>> = {
  kid: {
    badDriving: [
      "Hey! The road's the flat bit!",
      "My juice box just did a backflip!",
      "You're worse at this than my little brother!",
      "That was NOT a shortcut!",
      "I only get one head. Watch it!",
      "The seatbelt's doing all your work!",
      "Can we try driving BETWEEN the things?",
      "You owe my sandwich an apology!",
      "Even my toy taxi stays on the road!",
      "I'm telling Grandma about that one!",
    ],
    speeding: [
      "Faster! Wait. Less faster!",
      "This is awesome! Why am I scared?",
      "We've gone past my brave speed!",
      "My stomach's still at the last corner!",
      "Okay, I take back the faster thing!",
      "Can you turn the speed down one?",
      "Are we racing? Does Mum know?",
      "The buildings are doing zoomies!",
      "I can't count the shops this fast!",
      "Cool! Cool! BRAKES ARE ALSO COOL!",
    ],
    tsunami: [
      "That wave is bigger than the swimming pool!",
      "This taxi can swim, right? RIGHT?",
      "I left my floaties at home!",
      "The ocean's coming out of its spot!",
      "Can we go somewhere more upstairs?",
      "That's way too much bath water!",
      "I vote we don't jump that!",
      "The beach is chasing us!",
      "Please tell me there's a boat button!",
      "Nope! That's a grown-up-sized wave!",
    ],
    earthquake: [
      "Who's shaking the whole city?",
      "The road's doing the jelly dance!",
      "I didn't order the bouncy taxi!",
      "Even the buildings have wobbly teeth!",
      "Can you tell the ground to stop?",
      "Is there a giant under the road?",
      "Those bricks are coming DOWN!",
      "Let's go where the tall stuff isn't!",
      "My knees are wobbling back!",
      "This is why I build with cushions!",
    ],
    volcano: [
      "The mountain's throwing a tantrum!",
      "That's not a normal flying rock!",
      "Hot potato! Really, REALLY hot potato!",
      "Can we not park in the lava bit?",
      "My school volcano never did THAT!",
      "The floor is lava! For real this time!",
      "I think that mountain needs a time-out!",
      "Don't catch the spicy rocks!",
      "I can toast my sandwich from here!",
      "Let's visit a quieter mountain!",
    ],
    cyclone: [
      "The sky's got a giant hoover!",
      "Don't let it suck up my shoes!",
      "That cloud's touching the floor!",
      "I like spinning. Not TAXI spinning!",
      "Is there an off switch for wind?",
      "That's a very bad place to fly a kite!",
      "Can we outrun the swirly thing?",
      "My hat wants to leave without me!",
      "I don't want to be a flying taxi!",
      "Hide behind something bigger than us!",
    ],
  },
  grandma: {
    badDriving: [
      "The road, dear. Use the road.",
      "I've had smoother rides in a wheelbarrow.",
      "That was my good hip, you know.",
      "My knitting now has an extra sleeve.",
      "Do apologise to that poor taxi.",
      "Your driving teacher owes you a refund.",
      "Steady hands, dear. This isn't a whisk.",
      "I packed biscuits, not biscuit crumbs.",
      "Must we meet every kerb personally?",
      "One more jolt and you're walking me home.",
    ],
    speeding: [
      "Slow down. The bakery won't run away.",
      "I've waited eighty years. I can wait a minute.",
      "My spectacles aren't rated for this speed.",
      "We are not late for our own parade.",
      "Less accelerator, more common sense.",
      "At this pace my tea will arrive before us.",
      "You can impress me by using the brakes.",
      "My grandson drives like this. On a computer.",
      "Steady, dear. I'd like to read the street signs.",
      "I asked for a taxi, not a knitting missile.",
    ],
    tsunami: [
      "That's rather more sea than I expected.",
      "Up the hill, dear. Briskly now.",
      "My umbrella is not up to that.",
      "The promenade seems to be following us.",
      "Keep the water outside, if you please.",
      "I knew I should have worn my wellies.",
      "Let's find a nice solid building to hide behind.",
      "I have no intention of swimming to bingo.",
      "Even my biggest towel wouldn't help.",
      "High ground first. Complaints afterwards.",
    ],
    earthquake: [
      "That's not my knees this time.",
      "The whole street needs a good sit-down.",
      "Away from those tall buildings, dear.",
      "My dentures are applauding without permission.",
      "I prefer my paving firmly attached.",
      "Mind the falling masonry. It's terribly rude.",
      "The city has a dreadful case of the shivers.",
      "Find an open space. Granny's orders.",
      "I've survived worse dances at the village hall.",
      "Keep calm, dear. And mind that brick.",
    ],
    volcano: [
      "That mountain has lost its temper.",
      "I said somewhere warm, not on fire.",
      "Mind the hot rocks, dear.",
      "My oven makes less fuss than this.",
      "We'll admire the view from much farther away.",
      "That's enough heat to ruin my cardigan.",
      "No, we shall not stop for a photograph.",
      "Someone ought to turn that mountain down.",
      "My biscuits are already baked, thank you.",
      "Let's leave before the road needs oven gloves.",
    ],
    cyclone: [
      "That wind is after my Sunday hat.",
      "A sturdy building, dear. Find one.",
      "I've seen tidier things inside my vacuum.",
      "We are not joining that dreadful dance.",
      "Hold onto something that belongs to the taxi.",
      "My hair took forty minutes this morning.",
      "I don't care for this outdoor tumble dryer.",
      "Go the other way. Granny insists.",
      "That cloud needs to mind its manners.",
      "Flying is extra, and I'm not paying.",
    ],
  },
  business: {
    badDriving: [
      "That collision was not in the agenda.",
      "Please stop restructuring the taxi.",
      "My coffee has filed a complaint.",
      "Can we circle back to competent driving?",
      "This is a very bumpy career move.",
      "You're damaging our working relationship.",
      "My laptop just became a floortop.",
      "That turn needs a performance review.",
      "I'd like fewer impacts this quarter.",
      "Please keep all four wheels on task.",
    ],
    speeding: [
      "I said urgent. I did not say airborne.",
      "We're exceeding every sensible target.",
      "This is not what I meant by fast growth.",
      "My meeting can start without my lunch arriving first.",
      "Please reduce our velocity budget.",
      "I'm moving this journey to the risk register.",
      "The deadline is flexible. My spine is less so.",
      "We can make up time in the introductions.",
      "I'm not approving this acceleration expense.",
      "Let's aim for punctual AND intact.",
    ],
    tsunami: [
      "That's a worrying rise in liquid assets.",
      "New agenda: find higher ground.",
      "This meeting is now very much remote.",
      "I don't think expenses cover a submarine.",
      "The waterfront is expanding too aggressively.",
      "Please execute the uphill strategy.",
      "My briefcase is not a flotation device.",
      "We're about to be in over our heads.",
      "I'd like to take this offline. And uphill.",
      "There appears to be a wave of cancellations.",
    ],
    earthquake: [
      "Our foundations are looking unstable.",
      "I meant shake up the team, not the city.",
      "Please avoid the falling overheads.",
      "Let's move this discussion into an open space.",
      "My quarterly figures are all over the floor.",
      "The market isn't the only thing wobbling.",
      "This is a fairly literal business disruption.",
      "Can we postpone the ground movement?",
      "Risk assessment: don't park under bricks.",
      "I'm scheduling a very firm complaint to geology.",
    ],
    volcano: [
      "That is not a sustainable growth model.",
      "Our risk forecast now includes hot rocks.",
      "I'm cancelling the mountain retreat.",
      "The situation has become unnecessarily heated.",
      "Please keep my invoices away from the lava.",
      "This hotspot has terrible networking potential.",
      "Let's put some distance between us and that eruption.",
      "That's an alarming amount of overhead coming down.",
      "My insurance form needs another page.",
      "I'd prefer a route with fewer molten obstacles.",
    ],
    cyclone: [
      "That is excessive staff turnover.",
      "I'd rather not be swept into this project.",
      "Our exit strategy should start now.",
      "This is the wrong kind of upward mobility.",
      "My paperwork is attempting to resign.",
      "Let's shelter behind a more stable investment.",
      "That cloud is spinning the facts AND the cars.",
      "Please keep this business trip ground-based.",
      "I'm putting a hold on all further rotation.",
      "The forecast did not disclose this level of disruption.",
    ],
  },
  dog: {
    badDriving: [
      "Yelp! Bad driver. BAD.",
      "Woof! Mind the hat!",
      "Grrr. Gentle with the good dog.",
      "Bark! Seats are for sitting, not bouncing!",
      "No treat for that turn.",
      "Whine. My tail was comfortable there.",
      "Woof! Road. Stay. Good road.",
      "Yip! You nearly un-hatted me!",
      "Bad taxi! No biscuit!",
      "Grrr. I could steer better. With paws.",
    ],
    speeding: [
      "Woof! Too many smells at once!",
      "Ears back. Hat gone soon.",
      "Slow! Can't sniff!",
      "Whine. Walkies speed, please.",
      "I'm a dog, not a greyhound bus!",
      "Woof! The park went past too fast!",
      "Good driver. Heel. HEEL.",
      "My tongue is flapping indoors!",
      "Yip! This is faster than squirrels!",
      "Less zoom. More sniff.",
    ],
    tsunami: [
      "Woof? BIG bath?",
      "No bath! No giant bath!",
      "Paddle paws not ready!",
      "Bark! Sea, stay! STAY!",
      "My hat is dry-clean only. Woof.",
      "Whine. The water's chasing the car.",
      "Hill! Good hill! Go hill!",
      "That's too much fetch water!",
      "Woof! Windows up. ALL up.",
      "I am not shaking all that off.",
    ],
    earthquake: [
      "Grrr. Ground growling back.",
      "Woof! Who shook the pavement?",
      "Paws feel funny. Don't like.",
      "Bark! Buildings, stay!",
      "That's not my leg doing the kicking.",
      "Whine. The road won't sit.",
      "Hat vibrating. Assistance required.",
      "Woof! Falling bricks are not fetch sticks!",
      "Open space! Like park! Find park!",
      "I did not bury anything that big.",
    ],
    volcano: [
      "Woof! Spicy mountain!",
      "Hot rocks. Not tennis balls.",
      "Nose says NOPE.",
      "Bark! Mountain, stop throwing!",
      "Smells like burnt everything.",
      "Paws off the lava. Very paws off.",
      "Whine. Too hot for hat.",
      "No fetch. Definitely no fetch.",
      "Grrr. Bad mountain. Stay back.",
      "Woof! Find the cold road!",
    ],
    cyclone: [
      "Bark! Giant invisible vacuum!",
      "Hat! Save hat!",
      "Woof! Cloud doing zoomies!",
      "No flying walkies!",
      "Ears have become wings. Help.",
      "Grrr. Wind stole my smell.",
      "Sit, tornado! SIT!",
      "Whine. Too much hair dryer.",
      "Woof! Big wall. Hide there.",
      "Chase tail, not taxi!",
    ],
  },
  tourist: {
    badDriving: [
      "Is crashing a local tradition?",
      "That photo is mostly my own elbow.",
      "The guidebook promised a gentle tour!",
      "I'd like to see the sights from inside the taxi.",
      "Please stop introducing us to the scenery!",
      "Does the souvenir shop sell neck cushions?",
      "That bump won't improve your review.",
      "I booked sightseeing, not kerb tasting.",
      "Could we admire the buildings without touching them?",
      "My postcard now says 'Wish you were careful.'",
    ],
    speeding: [
      "All my photos are horizontal stripes!",
      "Could we see the city at reading speed?",
      "Was that the museum? Was that everything?",
      "I'm collecting memories, not windburn!",
      "This tour is becoming a slideshow on fast-forward.",
      "I can't wave at anybody this quickly!",
      "The scenic route needs more scenic time.",
      "Please slow down. My map is frightened.",
      "I wanted an adventure with pauses!",
      "At this speed I'll need to visit twice!",
    ],
    tsunami: [
      "The brochure said waterfront, not waterEVERYWHERE!",
      "Is that wave included in the city pass?",
      "Perhaps the hill has a better view!",
      "I think the beach is coming to collect us.",
      "That's a spectacular reason to leave!",
      "My camera is splash-proof, not ocean-proof!",
      "Let's take the uphill tour immediately.",
      "I wasn't dressed for the underwater district!",
      "Could we photograph that from somewhere dry?",
      "Five stars for the wave. Zero for our distance from it.",
    ],
    earthquake: [
      "Is the walking tour usually this wobbly?",
      "Even the landmarks won't hold still!",
      "That building is dropping souvenirs!",
      "I think the map is rearranging itself.",
      "Let's visit a nice empty square!",
      "The brochure forgot the moving pavement.",
      "My camera's stabiliser has given up.",
      "I wanted local flavour, not local tremors!",
      "Are those historic bricks? Let's not catch them.",
      "The whole city is shaking hands with us!",
    ],
    volcano: [
      "Oh! It really IS an active volcano!",
      "The postcard version looked much calmer.",
      "This is slightly too close for the zoom lens.",
      "I'd prefer my souvenirs room temperature.",
      "Is there a viewing platform farther away?",
      "That mountain has a very dramatic welcome show.",
      "I'm crossing 'touch the lava' off my itinerary.",
      "A lovely photo. Let's leave before the next rock.",
      "The hot springs have rather overdone it.",
      "Could we take the non-erupting route?",
    ],
    cyclone: [
      "What an impressive cloud! Let's go away from it.",
      "My map's trying to take its own tour!",
      "Is this the famous whirlwind visit?",
      "I didn't book the aerial upgrade!",
      "That's a terrible place for a photo stop.",
      "The gift bag has achieved flight!",
      "Can we view the cyclone from behind a building?",
      "I prefer attractions that don't follow me.",
      "My travel insurance just flashed before my eyes.",
      "Lovely weather for leaving this exact spot!",
    ],
  },
  chef: {
    badDriving: [
      "My soufflé! You absolute speed bump!",
      "We are transporting lunch, not tossing salad!",
      "Gentle hands! This taxi is not a mixing bowl!",
      "You've scrambled my perfectly good eggs!",
      "That corner was badly executed AND underseasoned!",
      "My cake now has a leaning tower tier!",
      "Less banging, more driving, chef!",
      "Do not tenderise the passenger!",
      "I asked for a smooth ride, not chunky!",
      "You've turned my lunch into a floor special!",
    ],
    speeding: [
      "Quick service does not mean rocket service!",
      "My soup is climbing out of its container!",
      "Slow down! The soufflé needs dignity!",
      "We are late, not being flambéed!",
      "This is too fast even for fast food!",
      "Take your foot off the gas, not off the planet!",
      "I can't plate anything at this velocity!",
      "Simmer down, driver. Literally less heat!",
      "My garnish is experiencing lift-off!",
      "I said chop-chop, not warp speed!",
    ],
    tsunami: [
      "That is far too much water for this recipe!",
      "Get uphill! My pastry hates damp!",
      "The sea has boiled over without boiling!",
      "We are not poaching the taxi!",
      "Salt water does NOT improve a soufflé!",
      "I ordered fresh fish, not the entire ocean!",
      "Find a dry route before lunch becomes soup!",
      "That's a tidal wave of poor menu choices!",
      "My kitchen towels cannot fix this!",
      "Higher ground! I refuse to serve soggy everything!",
    ],
    earthquake: [
      "Who put the city on the mixer?",
      "Stop whisking the road!",
      "My soufflé cannot take this level of enthusiasm!",
      "Those falling bricks are not croutons!",
      "Find open ground. The buildings are shedding!",
      "The pavement has the consistency of jelly!",
      "I did not request shaken custard!",
      "Everything is coming off the menu AND the shelves!",
      "This is why I insist on a stable work surface!",
      "Too much movement! Let the city rest!",
    ],
    volcano: [
      "Someone has left the mountain on full heat!",
      "That is an outrageous flambé!",
      "The road is not an oven tray!",
      "Burnt rocks are not a garnish!",
      "Turn away! I can smell my eyebrows cooking!",
      "I prefer a controlled flame, thank you!",
      "This kitchen is far too open-plan!",
      "My soufflé is getting a second bake!",
      "Hot behind! Hot in front! Hot everywhere!",
      "Send it back. The mountain is overdone!",
    ],
    cyclone: [
      "That cloud is using the whisk attachment!",
      "We are not ingredients in a blender!",
      "My chef's hat has enough height already!",
      "Away from the enormous salad spinner!",
      "That's too much ventilation for any kitchen!",
      "Find shelter before my lunch becomes airborne catering!",
      "The wind is stealing my garnish!",
      "I said fold gently, not spin violently!",
      "This recipe does not call for flying taxis!",
      "Who ordered the whirlwind special? Cancel it!",
    ],
  },
  hero: {
    badDriving: [
      "Citizen! The city needs fewer dents!",
      "That was not heroic cornering!",
      "My superpower is not neck support!",
      "Let's protect the pedestrians AND the paintwork!",
      "Even a sidekick knows where the kerb is!",
      "Save the smashing for imaginary villains!",
      "You have defeated another perfectly innocent bumper!",
      "My heroic pose doesn't work face-first in the seat!",
      "Justice prefers a smoother ride!",
      "Careful! This is my civilian spine!",
    ],
    speeding: [
      "Heroic urgency has a speed limit!",
      "I said step on it, not bend time!",
      "Even I slow down for corners, citizen!",
      "My cape isn't here to act as a parachute!",
      "We can't save the city if we overshoot it!",
      "I'm detecting a dangerous shortage of braking!",
      "This is faster than my dramatic entrance!",
      "Let's arrive heroically, on all four wheels!",
      "The villains can wait through one sensible corner!",
      "My super senses say: less accelerator!",
    ],
    tsunami: [
      "Citizen, to higher ground!",
      "That's more water than my utility belt can handle!",
      "I'm powerful, not a plug for the ocean!",
      "Our heroic retreat begins uphill!",
      "Quick! Put a building between us and that wave!",
      "I should have brought the aquatic cape!",
      "Protect the passenger. That's me in this scenario!",
      "We shall confront the sea from somewhere dry!",
      "My weakness appears to be enormous waves!",
      "Upward, citizen! The sensible kind of upward!",
    ],
    earthquake: [
      "The city needs steadier foundations!",
      "I assure you, that was not my dramatic landing!",
      "To open ground, citizen! Mind the debris!",
      "I can't punch an earthquake. I've considered it!",
      "Even my heroic knees are wobbling!",
      "Keep clear of buildings doing their own stunts!",
      "The pavement has turned against justice!",
      "My super balance is working overtime!",
      "Falling masonry! Evasive driving, please!",
      "Let's save the speeches until the ground stops!",
    ],
    volcano: [
      "That mountain has adopted a villainous attitude!",
      "Evasive action! The sky is throwing rocks!",
      "My suit is dramatic, not ovenproof!",
      "We need distance, citizen, not a closer look!",
      "I left the lava boots with the cape!",
      "This is too much heat for my secret identity!",
      "I cannot arrest a mountain!",
      "Watch those shadows. Heroic advice, free of charge!",
      "Retreat is strategy when the road is on fire!",
      "The volcano can keep its very hot autograph!",
    ],
    cyclone: [
      "That is not an authorised method of flight!",
      "Find shelter, citizen! Even heroes use buildings!",
      "My dramatic hair cannot withstand this!",
      "We must escape the spin cycle of injustice!",
      "I'm fairly sure the cloud started it!",
      "Keep the taxi grounded. Leave flying to professionals!",
      "My wind resistance is mostly confidence!",
      "Turn away before we become a weather report!",
      "I've fought villains with less pull than that!",
      "A strategic wall would be heroic right now!",
    ],
  },
  robot: {
    badDriving: [
      "COLLISION AVOIDANCE: APPARENTLY OPTIONAL.",
      "DRIVER RATING REDUCED. AGAIN.",
      "PLEASE STOP REARRANGING MY COMPONENTS.",
      "ROUTE COMPUTED. WALL NOT INCLUDED.",
      "SUSPENSION COMPLAINT ADDED TO QUEUE.",
      "MY SCREWS HAVE FILED FOR SEPARATION.",
      "SMOOTH RIDE NOT FOUND. RETRY?",
      "ERROR: DRIVER MISTAKEN FOR PINBALL.",
      "RECOMMENDATION: DRIVE AROUND OBJECTS.",
      "I HAVE LOGGED THAT. IN CAPITAL LETTERS.",
    ],
    speeding: [
      "VELOCITY EXCEEDS MY COMFORT SETTINGS.",
      "PLEASE INSTALL A SLOWER DRIVER.",
      "SCENERY BUFFER OVERFLOW.",
      "ARRIVAL TIME: SOON. ARRIVAL CONDITION: UNCERTAIN.",
      "BRAKE PEDAL DETECTED. SUGGEST USING IT.",
      "MY PROCESSOR IS NOT THE ONLY THING OVERCLOCKED.",
      "SPEED LIMIT IS NOT A HIGH SCORE.",
      "REQUESTING FIRMWARE UPDATE: COMMON SENSE.",
      "I CAN CALCULATE THIS CORNER. YOU CANNOT MAKE IT.",
      "PLEASE REDUCE SPEED BEFORE I BECOME SPARE PARTS.",
    ],
    tsunami: [
      "WATER DETECTED. TOO MUCH WATER DETECTED.",
      "WATERPROOF RATING: EMBARRASSINGLY LOW.",
      "NEW DESTINATION: ANYWHERE HIGHER.",
      "OCEAN BOUNDARY ERROR.",
      "THIS MODEL DOES NOT INCLUDE SWIM MODE.",
      "RECOMMENDATION: KEEP THE SEA OUTSIDE.",
      "SHORT CIRCUIT FORECAST: INCREASING.",
      "WAVE HEIGHT EXCEEDS TAXI HEIGHT. UNFAVOURABLE.",
      "PLEASE DO NOT RINSE THE PASSENGER.",
      "DRY ROUTE REQUESTED WITH EXTREME PRIORITY.",
    ],
    earthquake: [
      "GROUND STABILITY CHECK FAILED.",
      "THIS IS NOT MY VIBRATION SETTING.",
      "FALLING DEBRIS DETECTED. DUCK FUNCTION UNAVAILABLE.",
      "CITY REQUIRES ADDITIONAL SCREWS.",
      "GYROSCOPE REQUESTING A HOLIDAY.",
      "PLEASE NAVIGATE TO FEWER OVERHEAD OBJECTS.",
      "MAP COORDINATES ARE WOBBLING.",
      "SEISMIC ACTIVITY IS NOT A MASSAGE FEATURE.",
      "RECALIBRATING. RECALIBRATING. STILL RECALIBRATING.",
      "OPEN SPACE IDENTIFIED AS PREFERRED WORKSPACE.",
    ],
    volcano: [
      "AMBIENT TEMPERATURE: ABSOLUTELY NOT.",
      "MOUNTAIN COOLING FAN HAS FAILED.",
      "INCOMING ROCKS ARE NOT SOFTWARE UPDATES.",
      "MY WARRANTY EXCLUDES LAVA.",
      "PLEASE EXIT THE EXTERNAL FURNACE.",
      "THERMAL WARNING. SECOND THERMAL WARNING.",
      "MOLTEN ROAD SURFACE NOT SUPPORTED.",
      "I AM ALREADY ASSEMBLED. DO NOT RE-SMELT.",
      "RECOMMENDED DISTANCE FROM ERUPTION: MORE.",
      "HEAT SINK CAPACITY: NOT AN ENTIRE VOLCANO.",
    ],
    cyclone: [
      "UNREQUESTED ROTATION DETECTED.",
      "WIND SPEED EXCEEDS HAT RETENTION LIMIT.",
      "FLIGHT MODE IS A PHONE SETTING, NOT A TAXI SETTING.",
      "PLEASE CANCEL THE SPIN CYCLE.",
      "SUCTION DETECTED. I AM NOT DUST.",
      "SHELTER SEARCH INITIATED. TRY A BUILDING.",
      "GYROSCOPE HAS SUBMITTED A RESIGNATION.",
      "ERROR: SKY ATTEMPTING TO DOWNLOAD TAXI.",
      "AERODYNAMIC PROFILE: UNHELPFULLY BOXY.",
      "ESCAPE VECTOR COMPUTED. AWAY FROM THAT.",
    ],
  },
};
